import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Coins, Loader2, Crown, Ticket, Search, ScanLine, CheckCircle2, X } from 'lucide-react';
import api from '../../lib/api';
import CloudinaryUploader from '../CloudinaryUploader';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';

const EMPTY = { title: '', description: '', image_url: '', points_cost: 500, stock: 0, subscribers_only: false, is_active: true, event_id: '', location_label: '', requires_ticket: false };

export const BravxBenefitsAdmin = () => {
  const [benefits, setBenefits] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [redSearch, setRedSearch] = useState('');
  const [markingId, setMarkingId] = useState(null);

  const refresh = () => {
    api.get('/fullbravx/admin/benefits').then(r => setBenefits(r.data.benefits || [])).catch(() => {});
    api.get('/fullbravx/admin/redemptions').then(r => setRedemptions(r.data.redemptions || [])).catch(() => {});
    api.get('/events/').then(r => setEvents(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };
  useEffect(refresh, []);

  const save = async () => {
    if (!form.title.trim() || !(form.points_cost > 0)) return toast.error('Título y costo en puntos (>0) obligatorios');
    setSaving(true);
    try {
      const payload = { ...form, requires_ticket: form.event_id ? form.requires_ticket : false };
      if (editingId) await api.put(`/fullbravx/admin/benefits/${editingId}`, payload);
      else await api.post('/fullbravx/admin/benefits', payload);
      toast.success(editingId ? 'Beneficio actualizado' : 'Beneficio creado');
      setForm(EMPTY); setEditingId(null); refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este beneficio?')) return;
    try { await api.delete(`/fullbravx/admin/benefits/${id}`); refresh(); toast.success('Eliminado'); }
    catch { toast.error('Error al eliminar'); }
  };

  const markUsed = async (key, { silent } = {}) => {
    setMarkingId(key);
    try {
      const r = await api.post(`/fullbravx/admin/redemptions/${encodeURIComponent(key)}/use`);
      if (r.data.already_used) {
        toast.warning('⚠️ Este código YA fue usado');
        setValidationResult({ ...r.data.redemption, _already_used: true });
      } else {
        if (!silent) toast.success('✅ Código canjeado correctamente');
        setValidationResult(r.data.redemption);
      }
      refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error al validar el código';
      toast.error(msg);
      setValidationResult({ _not_found: true, code: key });
    } finally { setMarkingId(null); }
  };

  const validateCode = () => {
    const key = codeInput.trim();
    if (!key) return toast.error('Ingresá un código');
    markUsed(key);
  };

  const filteredRedemptions = useMemo(() => {
    const q = redSearch.trim().toLowerCase();
    if (!q) return redemptions;
    return redemptions.filter(r =>
      (r.code || '').toLowerCase().includes(q) ||
      (r.user_email || '').toLowerCase().includes(q) ||
      (r.user_name || '').toLowerCase().includes(q) ||
      (r.benefit_title || '').toLowerCase().includes(q)
    );
  }, [redemptions, redSearch]);

  const activeCount = redemptions.filter(r => r.status !== 'used').length;

  return (
    <div className="space-y-6" data-testid="bravx-benefits-admin">
      {/* Form */}
      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-5 space-y-4">
        <h3 className="text-white font-bold flex items-center gap-2"><Coins size={16} className="text-[#D4AF37]" /> {editingId ? 'Editar beneficio' : 'Nuevo beneficio canjeable por puntos'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-[#A1A1AA]">Título *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej: Trago gratis en barra" className="bg-[#121212] border-[#27272A] text-white" data-testid="benefit-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[#A1A1AA]">Imagen del beneficio (opcional)</Label>
            {form.image_url ? (
              <div className="flex items-center gap-3">
                <img src={form.image_url} alt="" className="w-14 h-14 rounded-lg object-cover border border-[#27272A]" />
                <button onClick={() => setForm({ ...form, image_url: '' })} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1" data-testid="benefit-image-remove"><X size={12} /> Quitar</button>
              </div>
            ) : (
              <CloudinaryUploader
                folder="bravx_benefits"
                resourceType="image"
                accept="image/*"
                maxSize={8 * 1024 * 1024}
                buttonText="Subir imagen"
                showPreview={false}
                onUploadComplete={(result) => setForm(prev => ({ ...prev, image_url: result.url }))}
                data-testid="benefit-image-uploader"
              />
            )}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-[#A1A1AA]">Descripción</Label>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Qué incluye y cómo se usa el código" className="bg-[#121212] border-[#27272A] text-white" data-testid="benefit-description-input" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-[#A1A1AA]">Evento vinculado (opcional — aparece en la página del evento)</Label>
            <select
              value={form.event_id}
              onChange={(e) => setForm({ ...form, event_id: e.target.value, requires_ticket: e.target.value ? form.requires_ticket : false })}
              className="w-full bg-[#121212] border border-[#27272A] rounded-md px-3 py-2 text-white text-sm"
              data-testid="benefit-event-select"
            >
              <option value="">— Sin evento (beneficio general) —</option>
              {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[#A1A1AA]">Lugar de canje (opcional, ej: "Barra principal")</Label>
            <Input value={form.location_label} onChange={(e) => setForm({ ...form, location_label: e.target.value })} placeholder="Ej: Barra principal · Local X" className="bg-[#121212] border-[#27272A] text-white" data-testid="benefit-location-input" />
          </div>
        </div>
        {form.event_id && (
          <label className="flex items-center gap-2 text-xs text-[#A1A1AA] cursor-pointer bg-sky-500/5 border border-sky-500/20 rounded-lg px-3 py-2.5 w-fit">
            <Switch checked={form.requires_ticket} onCheckedChange={(v) => setForm({ ...form, requires_ticket: v })} data-testid="benefit-requires-ticket-switch" />
            <span><span className="text-sky-300 font-bold">🎟 Requiere entrada del evento</span> — solo puede canjearlo quien haya comprado alguna entrada</span>
          </label>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs text-[#A1A1AA]">Costo en puntos *</Label>
            <Input type="number" min="1" value={form.points_cost} onChange={(e) => setForm({ ...form, points_cost: parseInt(e.target.value) || 0 })} className="bg-[#121212] border-[#27272A] text-white" data-testid="benefit-points-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[#A1A1AA]">Stock (0 = ilimitado)</Label>
            <Input type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: Math.max(0, parseInt(e.target.value) || 0) })} className="bg-[#121212] border-[#27272A] text-white" />
          </div>
          <label className="flex items-center gap-2 text-xs text-[#A1A1AA] cursor-pointer">
            <Switch checked={form.subscribers_only} onCheckedChange={(v) => setForm({ ...form, subscribers_only: v })} data-testid="benefit-subs-only-switch" />
            Solo suscriptores
          </label>
          <label className="flex items-center gap-2 text-xs text-[#A1A1AA] cursor-pointer">
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
            Activo
          </label>
        </div>
        <div className="flex gap-2">
          <button onClick={save} disabled={saving} className="px-5 py-2.5 bg-[#D4AF37] text-black rounded-lg text-sm font-bold flex items-center gap-2 hover:bg-[#F2D272]" data-testid="benefit-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {editingId ? 'Guardar cambios' : 'Crear beneficio'}
          </button>
          {editingId && (
            <button onClick={() => { setForm(EMPTY); setEditingId(null); }} className="px-4 py-2.5 border border-[#27272A] text-[#A1A1AA] rounded-lg text-sm">Cancelar</button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {benefits.length === 0 && <p className="text-sm text-[#52525B] italic">Todavía no creaste beneficios canjeables.</p>}
        {benefits.map(b => (
          <div key={b.id} className="flex items-center gap-3 bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4" data-testid={`benefit-row-${b.id}`}>
            {b.image_url && <img src={b.image_url} alt="" className="w-12 h-12 rounded-lg object-cover" />}
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm flex items-center gap-2 flex-wrap">
                {b.title}
                {b.subscribers_only && <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40 uppercase font-bold"><Crown size={9} /> Solo subs</span>}
                {b.requires_ticket && b.event_id && <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/40 uppercase font-bold">🎟 Req. entrada</span>}
                {!b.is_active && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-zinc-700 text-zinc-300 uppercase font-bold">Inactivo</span>}
              </p>
              <p className="text-xs text-[#71717A]">
                <span className="text-[#D4AF37] font-bold">{b.points_cost} pts</span>
                {' · '}{b.stock > 0 ? `Stock: ${b.redemptions_count || 0}/${b.stock}` : 'Sin límite'}
                {' · '}{b.redemptions_count || 0} canje(s)
                {b.event_id && <span className="text-sky-400"> · 🎫 {events.find(e => e.id === b.event_id)?.title || 'Evento'}</span>}
                {b.location_label && <span> · 📍 {b.location_label}</span>}
              </p>
            </div>
            <button onClick={() => { setForm({ title: b.title, description: b.description || '', image_url: b.image_url || '', points_cost: b.points_cost, stock: b.stock || 0, subscribers_only: !!b.subscribers_only, is_active: b.is_active !== false, event_id: b.event_id || '', location_label: b.location_label || '', requires_ticket: !!b.requires_ticket }); setEditingId(b.id); }} className="text-[#71717A] hover:text-white p-2"><Pencil size={15} /></button>
            <button onClick={() => remove(b.id)} className="text-[#71717A] hover:text-red-400 p-2" data-testid={`benefit-delete-${b.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
      </div>

      {/* Code validator */}
      <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-xl p-5" data-testid="code-validator-card">
        <h3 className="text-white font-bold flex items-center gap-2 mb-1"><ScanLine size={16} className="text-[#D4AF37]" /> Validar código de canje</h3>
        <p className="text-xs text-[#52525B] mb-3">Ingresá el código que muestra el cliente (ej: BRVX-A1B2C3). Si es válido, queda marcado como usado.</p>
        <div className="flex gap-2">
          <Input
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && validateCode()}
            placeholder="BRVX-XXXXXX"
            className="bg-[#121212] border-[#27272A] text-white font-mono tracking-widest max-w-xs"
            data-testid="code-validator-input"
          />
          <button onClick={validateCode} disabled={markingId === codeInput.trim()} className="px-5 py-2 bg-[#D4AF37] text-black rounded-lg text-sm font-bold flex items-center gap-2 hover:bg-[#F2D272]" data-testid="code-validator-btn">
            {markingId === codeInput.trim() ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Validar y canjear
          </button>
        </div>
        {validationResult && (
          <div className={`mt-4 rounded-lg p-4 border ${validationResult._not_found ? 'border-red-500/40 bg-red-500/5' : validationResult._already_used ? 'border-amber-500/40 bg-amber-500/5' : 'border-emerald-500/40 bg-emerald-500/5'}`} data-testid="code-validation-result">
            {validationResult._not_found ? (
              <p className="text-sm text-red-400 font-bold">❌ Código "{validationResult.code}" no encontrado</p>
            ) : (
              <>
                <p className={`text-sm font-bold ${validationResult._already_used ? 'text-amber-400' : 'text-emerald-400'}`}>
                  {validationResult._already_used ? `⚠️ Código YA usado${validationResult.used_at ? ` el ${new Date(validationResult.used_at).toLocaleString('es-AR')}` : ''}` : '✅ Canje válido — marcado como usado'}
                </p>
                <p className="text-xs text-[#A1A1AA] mt-1">
                  <span className="text-white font-semibold">{validationResult.benefit_title}</span>
                  {' · '}{validationResult.user_name || validationResult.user_email}
                  {' · '}{validationResult.points_spent} pts
                  {' · '}<span className="font-mono text-[#D4AF37]">{validationResult.code}</span>
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Redemptions */}
      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-5">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h3 className="text-white font-bold flex items-center gap-2"><Ticket size={15} className="text-[#D4AF37]" /> Canjes ({redemptions.length}) <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5">{activeCount} pendientes</span></h3>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#52525B]" />
            <Input value={redSearch} onChange={(e) => setRedSearch(e.target.value)} placeholder="Buscar código, email, beneficio…" className="bg-[#121212] border-[#27272A] text-white pl-8 h-8 text-xs w-64" data-testid="redemptions-search-input" />
          </div>
        </div>
        {filteredRedemptions.length === 0 ? <p className="text-sm text-[#52525B] italic">Sin canjes{redSearch ? ' que coincidan con la búsqueda' : ' todavía'}.</p> : (
          <div className="max-h-96 overflow-y-auto space-y-1.5">
            {filteredRedemptions.map(r => (
              <div key={r.id} className="flex items-center justify-between gap-3 text-xs border-b border-[#1a1a1a] pb-2 pt-0.5" data-testid={`redemption-row-${r.id}`}>
                <div className="min-w-0">
                  <span className="text-white font-semibold">{r.benefit_title}</span>
                  <span className="text-[#71717A]"> · {r.user_name || r.user_email}</span>
                  <div className="text-[#52525B] mt-0.5">
                    <span className="font-mono text-[#D4AF37] font-bold">{r.code}</span>
                    {' · '}{r.points_spent} pts
                    {' · '}{new Date(r.created_at).toLocaleDateString('es-AR')}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {r.status === 'used' ? (
                    <span className="text-[9px] px-2 py-1 rounded-full bg-zinc-700/60 text-zinc-300 uppercase font-bold" title={r.used_at ? `Usado el ${new Date(r.used_at).toLocaleString('es-AR')}` : ''}>Usado</span>
                  ) : (
                    <button
                      onClick={() => window.confirm(`¿Marcar como usado el código ${r.code}?`) && markUsed(r.code)}
                      disabled={markingId === r.code}
                      className="text-[9px] px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 uppercase font-bold hover:bg-emerald-500/25"
                      data-testid={`mark-used-${r.id}`}
                    >
                      {markingId === r.code ? '…' : 'Canjear'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
